// frontend/src/features/workspace/components/ModelCard.tsx
import { useState, type JSX } from 'react'
import { Copy, Trash2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatRelativeTime } from '@/lib/format'
import { useDeleteModel } from '../api/use-models'
import type { Capability } from '@shared/collaboration/access'
import { ShareButton } from '@/features/collaboration/components/ShareButton'

interface ModelCardProps {
  id: string
  projectId: string
  projectName: string
  name: string
  sourceType: string
  pipeCount: number
  calcStatus: string
  calcSummary: { iterations?: number; warningsCount?: number } | null
  updatedAt: string
  /** Hide the project-name badge — pass false on pages already scoped to one project. */
  showProjectBadge?: boolean
  /**
   * Undefined on call sites whose data source doesn't resolve access (e.g.
   * DashboardPage's recent-models list) — ShareButton fails closed and
   * hides itself rather than guessing at the caller's capabilities.
   */
  capabilities?: Capability[] | undefined
  /** When provided, renders a Clone action in the card's icon cluster. */
  onClone?: () => void
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  'aft-fathom': 'AFT import',
  'dxf': 'DXF import',
  'revit-ifc': 'IFC import',
  'revit-schedule': 'Revit import',
  'pipe-flo': 'PIPE-FLO import',
  'json': 'JSON import',
  'pipe-flo-desktop-json': 'Desktop import',
  'manual': 'Manual',
}

/** @internal exported for test — every calcStatus value must be handled here. */
export function getCalcStatusConfig(status: string, summary: ModelCardProps['calcSummary']): {
  color: string
  borderColor: string
  label: string
} {
  switch (status) {
    case 'converged':
      return {
        color: 'text-green-500',
        borderColor: 'border-l-green-500',
        label: `Converged${summary?.iterations ? ` · ${summary.iterations} iter` : ''}`,
      }
    case 'warnings':
      return {
        color: 'text-amber-500',
        borderColor: 'border-l-amber-500',
        label: `${summary?.warningsCount ?? 0} warnings`,
      }
    case 'partial':
      // Some Systems were skipped, but the ones that ran produced real results.
      // Left to the default branch this reads "Not calculated", which is the
      // opposite of true.
      return {
        color: 'text-amber-500',
        borderColor: 'border-l-amber-500',
        label: 'Partial',
      }
    case 'failed':
      return {
        color: 'text-red-500',
        borderColor: 'border-l-red-500',
        label: 'Failed to converge',
      }
    default:
      return {
        color: 'text-muted-foreground',
        borderColor: 'border-l-muted-foreground/30',
        label: 'Not calculated',
      }
  }
}

export const ModelCard = ({
  id,
  projectId,
  projectName,
  name,
  sourceType,
  pipeCount,
  calcStatus,
  calcSummary,
  updatedAt,
  showProjectBadge = true,
  capabilities,
  onClone,
}: ModelCardProps): JSX.Element => {
  const statusConfig = getCalcStatusConfig(calcStatus, calcSummary)
  const deleteModel = useDeleteModel()
  const [confirming, setConfirming] = useState(false)

  const handleDelete = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    if (!confirming) {
      setConfirming(true)
      return
    }
    deleteModel.mutate({ projectId, modelId: id })
  }

  const handleCancelDelete = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    setConfirming(false)
  }

  const handleClone = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    onClone?.()
  }

  return (
    <Link to={`/projects/${projectId}/models/${id}`} className="block group relative">
      <Card className={cn(
        'border-l-4 transition-shadow hover:shadow-md cursor-pointer',
        statusConfig.borderColor,
      )}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold leading-tight truncate">{name}</h3>
            <div className="flex items-center gap-0.5 shrink-0">
              {showProjectBadge && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 mr-1">
                  {projectName}
                </Badge>
              )}
              <ShareButton
                resourceType="model"
                resourceId={id}
                resourceName={name}
                capabilities={capabilities}
              />
              {onClone && (
                <button
                  type="button"
                  onClick={handleClone}
                  title="Clone model"
                  className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-primary/10 hover:text-primary"
                >
                  <Copy className="h-3.5 w-3.5" />
                  <span className="sr-only">Clone</span>
                </button>
              )}
              {confirming ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white hover:bg-red-700"
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleDelete}
                  title="Delete model"
                  className="rounded-md p-1 text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="sr-only">Delete</span>
                </button>
              )}
            </div>
          </div>

          <p className="mt-1.5 text-xs text-muted-foreground">
            {pipeCount} pipe{pipeCount !== 1 ? 's' : ''} · {SOURCE_TYPE_LABELS[sourceType] ?? sourceType}
          </p>

          <div className="mt-2 flex items-center justify-between">
            <span className={cn('text-xs font-medium', statusConfig.color)}>
              {statusConfig.label}
            </span>
            <span className="text-[10px] text-muted-foreground">
              Edited {formatRelativeTime(updatedAt)}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

export const NewModelCard = ({ onClick }: { onClick?: () => void }): JSX.Element => {
  const content = (
    <Card className="border-2 border-dashed border-muted-foreground/20 transition-colors hover:border-muted-foreground/40 cursor-pointer h-full">
      <CardContent className="flex h-full items-center justify-center p-6">
        <div className="text-center">
          <span className="text-2xl text-muted-foreground/40">+</span>
          <p className="mt-1 text-sm font-medium text-muted-foreground">New Model</p>
        </div>
      </CardContent>
    </Card>
  )

  if (onClick) {
    return <button onClick={onClick} className="block w-full text-left">{content}</button>
  }
  return <Link to="/import" className="block">{content}</Link>
}
